import classNames from "../../../../utils/classNames"
import { formatMoney, formatMoneyFromCents } from "~/utils/formatMoney"
import Form from "../../../ui/Admin/Form"
import { EllipsisHorizontalIcon } from "@heroicons/react/24/solid"
import DropdownButton from "../../../ui/Admin/DropdownButton"
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon as CurrencyDollarIconOutline,
} from "@heroicons/react/24/outline"
import { formatDateRange } from "~/utils/formatDateRange"
import IInvoice from "../../../../interfaces/IInvoice"
import { RowItem } from "../Table"
import { INVOICE_STATUSES } from "~/decorators/InvoiceDecorator"
import roundToMaxPrecision from "../../../../utils/roundToMaxPrecision"
import parseDateTime from "../../../../utils/parseDateTime"
import { CurrencyDollarIcon } from "@heroicons/react/20/solid"
import { getSendDateIcon, getSendDateTooltipHelperText, RecurringKindBadgeFor, statusBadgeFor } from "./row.utils"
import { useTableRow } from "./useTableRow"
import PortalHoverTip from "~/components/ui/PortalHoverTip"

interface ComponentProps {
  rowItem: RowItem
  refreshLocalInvoices: () => void
  index: number
  isSelected: boolean
  setIsSelected: (invoice: IInvoice, isSelected: boolean) => void
  setSelectedInvoice: (invoice: IInvoice) => void
  isVisibleElectric: boolean
  isMeterFieldsEditable: boolean
  gridTemplateColumns: string
}

const Row = (props: ComponentProps) => {
  const {
    isMissingContactInfo,
    thisRowItem,
    thisIndex,
    camp,
    selectable,
    isShowingElectricFields,
    isChanged,
    hasSendDate,
    formattedDate,
    isDueDateRed,
    onChangeLastMeterReading,
    onChangeMeterReading,
    onChangeElectricUsed,
    dropdownOptionsFor,
    setValue,
  } = useTableRow({
    rowItem: props.rowItem,
    isVisibleElectric: props.isVisibleElectric,
    setSelectedInvoice: props.setSelectedInvoice,
    refreshLocalInvoices: props.refreshLocalInvoices,
  })

  return (
    <div
      style={{
        gridTemplateColumns: props.gridTemplateColumns,
      }}
      className={classNames(
        " transition-all duration-100 grid overflow-hidden 2xl:overflow-visible w-full border-b border-gray-200",
        selectable && "hover:bg-gray-50 ",
        !props.isMeterFieldsEditable && "hover:cursor-pointer",
        props.isSelected ? "bg-gray-50 border-l-1.5 border-l-green-600" : undefined,
      )}
      onClick={() => selectable && props.setIsSelected(thisRowItem.invoice, !props.isSelected)}
    >
      <div className="flex justify-center items-center">
        {!props.isMeterFieldsEditable && (
          <input
            type="checkbox"
            className={classNames(
              "transition-all duration-100 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-600",
              !selectable && "bg-gray-50 border-gray-100 hover:cursor-not-allowed",
              selectable && "hover:bg-gray-100 hover:cursor-pointer hover:border-gray-400",
            )}
            checked={props.isSelected}
            onChange={(e) => props.setIsSelected(thisRowItem.invoice, e.target.checked)}
            disabled={!selectable}
          />
        )}
      </div>
      <div className="whitespace-nowrap text-sm text-gray-500 flex items-center">
        {!thisRowItem.isSiteOnly && (
          <>
            {formatMoney(thisRowItem.amountOwed, camp.currency)}
            <Form.NumberInput hidden field={`rowItems.${thisIndex}.amountOwed`} />
          </>
        )}
      </div>
      <div className="whitespace-nowrap text-sm text-gray-500 flex items-center">
        {!thisRowItem.isSiteOnly && statusBadgeFor(thisRowItem.invoice)}
      </div>
      <div className="whitespace-nowrap text-sm text-gray-500 flex items-center">
        {thisRowItem.reservationRecurringKind && RecurringKindBadgeFor(thisRowItem.reservationRecurringKind)}
      </div>
      <div className="whitespace-pre-wrap text-sm flex items-center">
        <div className="font-medium text-gray-900">{thisRowItem.siteName}</div>
      </div>
      <div className="whitespace-nowrap text-sm text-gray-500 w-16 flex items-center">
        {!thisRowItem.isSiteOnly && (
          <>
            {isMissingContactInfo && (
              <PortalHoverTip className="-ml-6 mr-1" content="Missing contact info" xs>
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
              </PortalHoverTip>
            )}
            <span className="flex flex-col">
              <div className="font-medium text-gray-900">{thisRowItem.camperName}</div>
              <div className="font-normal text-gray-500">
                {formatDateRange(
                  thisRowItem.reservation?.start_date as string,
                  thisRowItem.reservation?.end_date as string,
                  camp.timezone,
                )}
              </div>
            </span>
          </>
        )}
      </div>
      <div className="whitespace-nowrap text-sm text-gray-500 flex items-center justify-center">
        {hasSendDate && (
          <>
            {formattedDate(thisRowItem.invoiceSendDate.toISOString())}{" "}
            {thisRowItem?.invoice?.send_date && thisRowItem?.invoice?.status && (
              <PortalHoverTip
                className="ml-2"
                xs
                content={getSendDateTooltipHelperText(thisRowItem.invoice.send_date, thisRowItem.invoice.status)}
              >
                {getSendDateIcon(thisRowItem.invoice.send_date, thisRowItem.invoice.status)}
              </PortalHoverTip>
            )}
          </>
        )}
      </div>
      <div className="whitespace-nowrap text-sm text-gray-500 flex items-center">
        <div className={`lg:w-1/3 ${isDueDateRed ? "text-red-500" : ""}`}>
          {!thisRowItem.isSiteOnly &&
            (thisRowItem.invoiceDueDate?.isValid() ? formattedDate(thisRowItem.invoiceDueDate.format()) : "")}
        </div>
        {thisRowItem.reservation?.kind !== null &&
          (thisRowItem.reservation?.long_term_reservation?.is_enabled_auto_charging ||
            thisRowItem?.invoice?.is_attempted_auto_charge) &&
          thisRowItem.invoice.status !== INVOICE_STATUSES.PAID && (
            <PortalHoverTip
              className="ml-2"
              xs
              content={
                thisRowItem.invoice.status === INVOICE_STATUSES.OVERDUE
                  ? `Autocharge enabled but did not charge on due date`
                  : `Autocharge enabled for ${parseDateTime(thisRowItem.invoice.due_date).toFormat("LLL dd")}${thisRowItem.invoice.status === INVOICE_STATUSES.DRAFT ? ", but is a draft and won't charge until queued." : ""}`
              }
            >
              {thisRowItem.invoice.status === INVOICE_STATUSES.DRAFT ? (
                <ExclamationCircleIcon className="w-5 h-5 text-yellow-600" />
              ) : thisRowItem.invoice.status === INVOICE_STATUSES.OVERDUE ? (
                <CurrencyDollarIconOutline className="w-5 h-5 text-yellow-600" />
              ) : (
                <CurrencyDollarIcon className="w-5 h-5" />
              )}
            </PortalHoverTip>
          )}
      </div>
      {props.isVisibleElectric && (
        <>
          <div className="whitespace-nowrap text-sm text-gray-500 pr-3 flex items-center">
            {isShowingElectricFields && !thisRowItem.isSiteOnly && props.isMeterFieldsEditable ? (
              <Form.NumberInput
                field={`rowItems.${thisIndex}.lastMeterReading`}
                suffix="kWh"
                className="w-full"
                onChange={onChangeLastMeterReading}
                min={0}
                stopPropagation
                disabled={thisRowItem.hasMeterLocationId}
              />
            ) : (
              <div className="">{Number(thisRowItem.lastMeterReading)}</div>
            )}
          </div>
          <div className="whitespace-nowrap text-sm text-gray-500 pr-3 flex items-center">
            {isShowingElectricFields &&
              (props.isMeterFieldsEditable ? (
                <Form.NumberInput
                  field={`rowItems.${thisIndex}.currentMeterReading`}
                  suffix="kWh"
                  className="w-full"
                  onChange={onChangeMeterReading}
                  min={0}
                  stopPropagation
                  disabled={thisRowItem.hasMeterLocationId}
                />
              ) : (
                <div className="">{Number(thisRowItem.currentMeterReading)}</div>
              ))}
          </div>
          <div className="whitespace-nowrap text-sm text-gray-500 flex items-center pr-3">
            {isShowingElectricFields &&
              (props.isMeterFieldsEditable ? (
                <>
                  <Form.NumberInput
                    field={`rowItems.${thisIndex}.electricUsed`}
                    suffix="kWh"
                    className="w-full"
                    onChange={onChangeElectricUsed}
                    min={0}
                    stopPropagation
                    disabled={thisRowItem.hasMeterLocationId}
                  />
                  <Form.NumberInput
                    hidden
                    field={`rowItems.${thisIndex}.electricSubtotal`}
                    disabled={thisRowItem.hasMeterLocationId}
                  />
                </>
              ) : (
                <>
                  {roundToMaxPrecision(Number(thisRowItem.currentMeterReading) - Number(thisRowItem.lastMeterReading))}
                  <Form.NumberInput hidden field={`rowItems.${thisIndex}.electricUsed`} />
                  <Form.NumberInput hidden field={`rowItems.${thisIndex}.electricSubtotal`} />
                </>
              ))}
          </div>
          <div
            className={classNames(
              "whitespace-nowrap text-sm flex items-center",
              (isChanged(`rowItems.${thisIndex}`) && "text-gray-200") || "text-gray-500 ",
            )}
          >
            {isShowingElectricFields && (
              <>
                {formatMoneyFromCents(thisRowItem.electricSubtotal, camp?.currency)}
                <Form.NumberInput hidden field={`rowItems.${thisIndex}.electricSubtotal`} />
              </>
            )}
          </div>
        </>
      )}
      <div className="font-medium sm:pr-3" onClick={(e) => e.stopPropagation()}>
        {!thisRowItem.isSiteOnly && !props.isMeterFieldsEditable ? (
          <DropdownButton stopPropagation options={dropdownOptionsFor(thisRowItem.invoice)} isMenuFixed zIndex="z-0">
            <EllipsisHorizontalIcon className="rounded-md p-2 w-10 h-10 transition-colors duration-100 hover:bg-gray-100 text-gray-600 cursor-pointer" />
          </DropdownButton>
        ) : !!thisRowItem.hasMeterLocationId ? (
          <div className="h-full flex justify-center items-center">
            <input
              type="checkbox"
              className={classNames(
                "transition-all duration-100 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-600",
                !selectable && "bg-gray-50 border-gray-100 hover:cursor-not-allowed",
                selectable && "hover:bg-gray-100 hover:cursor-pointer hover:border-gray-400",
              )}
              checked={thisRowItem.isSyncingMeterReading}
              onChange={(e) => setValue(`rowItems.${thisIndex}.isSyncingMeterReading`, e.target.checked)}
              disabled={!selectable}
            />
            <span className="ml-2 text-sm text-gray-400">Sync</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default Row
